import { QueryClient } from "@tanstack/react-query";
import { BaseQueryKey } from "../base-query-key.service";
import {
    QueryKeyRefetcherAfterPage,
    QueryKeyRefetcherAfterLocale,
    QueryKeyRefetcherAfterGroup,
    QueryKeyRefetcherAfterScopes,
    QueryKeyRefetcherAfterId,
    QueryKeyRefetcherAfterArgs,
} from "./actors.type";

/**
 * Service class for refetching queries using the fluent builder pattern.
 *
 * This class extends BaseQueryKey to provide query refetching functionality.
 * It uses the fluent builder pattern to construct query keys for refetching cached queries.
 * The order is enforced: page > locale > group > scopes > id > args.
 * ! IMPORTANT: The values passed to the refetcher should be consistent to avoid breaking the query cache.
 *
 * @example
 * const refetcher = new QueryKeyRefetcher(queryClient);
 * refetcher
 *   .page('/home')
 *   .locale('en')
 *   .group('user')
 *   .scopes(['profile', 'settings'])
 *   .id(123)
 *   .args({ filter: 'active' })
 *   .execute();
 */
class QueryKeyRefetcher extends BaseQueryKey<
    QueryKeyRefetcherAfterPage,
    QueryKeyRefetcherAfterLocale,
    QueryKeyRefetcherAfterGroup,
    QueryKeyRefetcherAfterScopes,
    QueryKeyRefetcherAfterId,
    QueryKeyRefetcherAfterArgs
> {
    private _queryClient: QueryClient;

    /**
     * Constructor for QueryKeyRefetcher.
     *
     * @param {QueryClient} queryClient - The query client instance from @tanstack/react-query.
     */
    constructor(queryClient: QueryClient) {
        super();
        this._queryClient = queryClient;
    }

    /**
     * Implementation of abstract method to create the fluent interface after page is set.
     *
     * @returns {QueryKeyRefetcherAfterPage} - Returns interface for next available methods.
     */
    protected createAfterPageInterface(): QueryKeyRefetcherAfterPage {
        return {
            locale: (locale: string) => this.locale(locale),
            group: (group: string) => this.group(group),
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after locale is set.
     *
     * @returns {QueryKeyRefetcherAfterLocale} - Returns interface for next available methods.
     */
    protected createAfterLocaleInterface(): QueryKeyRefetcherAfterLocale {
        return {
            group: (group: string) => this.group(group),
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after group is set.
     *
     * @returns {QueryKeyRefetcherAfterGroup} - Returns interface for next available methods.
     */
    protected createAfterGroupInterface(): QueryKeyRefetcherAfterGroup {
        return {
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after scopes is set.
     *
     * @returns {QueryKeyRefetcherAfterScopes} - Returns interface for next available methods.
     */
    protected createAfterScopesInterface(): QueryKeyRefetcherAfterScopes {
        return {
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after id is set.
     *
     * @returns {QueryKeyRefetcherAfterId} - Returns interface for next available methods.
     */
    protected createAfterIdInterface(): QueryKeyRefetcherAfterId {
        return {
            args: (...args: any[]) => this.args(...args),
            execute: () => this.execute(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after args is set.
     *
     * @returns {QueryKeyRefetcherAfterArgs} - Returns interface for next available methods.
     */
    protected createAfterArgsInterface(): QueryKeyRefetcherAfterArgs {
        return {
            execute: () => this.execute(),
        };
    }

    /**
     * Execute the query refetching.
     *
     * @returns {void}
     *
     * @example
     * const refetcher = new QueryKeyRefetcher(queryClient);
     * refetcher
     *   .page('/home')
     *   .locale('en')
     *   .group('user')
     *   .scopes(['profile', 'settings'])
     *   .id(123)
     *   .args({ filter: 'active' })
     *   .execute();
     * // Refetches queries matching:
     * [
     *   { page: '/home' },
     *   { language: 'en' },
     *   { group: 'user' },
     *   { scopes: ['profile', 'settings'] },
     *   { id: 123 },
     *   { optional: [{ filter: 'active' }] }
     * ]
     */
    public execute(): void {
        const queryKey = this.builder();
        this._queryClient.refetchQueries({ queryKey });
    }
}

export { QueryKeyRefetcher };
export default QueryKeyRefetcher;
